import { useRollbar } from '@rollbar/react'
import React, { useState } from 'react'
import { LocaleEnum } from '@systemeio/ui-frontend'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { LikeButton } from 'shared/components/like/like-button'
import useUser from 'shared/hooks/use-user'
import { timeSince } from 'shared/utils/date-time/time-since'
import { handleWithoutPropagating } from 'shared/utils/handle-without-propagating'
import { Comment } from 'modules/comments/components/comment'
import { CommentHighlightProvider } from 'modules/comments/contexts/comment-highlight'
import { usePostCommentsInfoContext } from 'modules/community/context/post-comments-info'
import { PostStatusEnum } from 'modules/community/enums/post-status-enum'
import {
  CommentsSearchInterface,
  PostInterface,
  SearchPostInterface,
  isSearchPostGuard,
} from 'modules/community/types/post-interface'
import { PostPinnedActions } from '../pinned-post/components/post-pinned-actions'
import { PostActionsMenu } from '../post/components/post-actions-menu'
import PostModal from '../post/post-modal'
import { PostAuthorInfo } from './author-info'
import PostCommentsInfo from './comments-info'
import { PostAnnounceLayout } from './components/post-announce-layout'

type PostAnnounceProps<T extends PostInterface | SearchPostInterface> = {
  post: T
  deletePost: () => Promise<void>
  deletePostAndBanUser: (removeRecentActivitiesChecked: boolean) => Promise<void>
  handleLikeClick?: () => void
  className?: string
  isPinnedPost?: boolean
  comments?: CommentsSearchInterface[]
}

function PostAnnounce<T extends PostInterface | SearchPostInterface>({
  post,
  deletePost,
  handleLikeClick,
  deletePostAndBanUser,
  isPinnedPost,
  comments,
}: PostAnnounceProps<T>) {
  const rollbar = useRollbar()
  const { t } = useTranslation()
  const router = useRouter()
  const { user } = useUser()
  const { commentsInfoMap } = usePostCommentsInfoContext()
  const { communityPath, topicPath: topicPathParam } = router.query
  const communityPathWithSlug = `/community/${communityPath}`
  const topicPath = `/community/${communityPath}/${post.topic.path}`
  const postDetailsPath = `${topicPath}/${post.path}`
  const [opened, setOpened] = useState(false)
  const [commentsHighlightContext, setCommentsHighlightContext] = useState<{
    targetCommentId?: number
    searchHighlightedComments?: CommentsSearchInterface[]
  }>()

  const isSearchPost = isSearchPostGuard(post)

  const handleModalOpen = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const target = e.target as HTMLElement
    const isLink = target.tagName.toLowerCase() === 'a'

    if (!isLink) {
      if (window) {
        window.history.replaceState(null, '', postDetailsPath)
      }
      setOpened(true)
    }
  }

  const handleModalClose = () => {
    if (window) {
      window.history.replaceState(null, '', topicPathParam ? topicPath : communityPathWithSlug)
    }
    setOpened(false)
    setCommentsHighlightContext(undefined)
  }

  const handleCommentClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation()
    const commentId = (e.currentTarget as HTMLElement).dataset.commentId
    const numericalCommentId = parseInt(commentId ?? '0', 10)
    !!numericalCommentId &&
      !isNaN(numericalCommentId) &&
      setCommentsHighlightContext({
        targetCommentId: numericalCommentId,
        searchHighlightedComments: comments,
      })
    handleModalOpen(e)
  }

  const commentsInfo = commentsInfoMap[post.id]

  if (post.status !== PostStatusEnum.Pending && !post.publishedAt) {
    rollbar.error('Regular post has no publishedAt', { post })
    return null
  }

  return (
    <PostAnnounceLayout
      postData={post}
      isPinnedPost={isPinnedPost}
      isNeedTruncate={true}
      onClick={handleModalOpen}
      headerTitle={
        <PostAuthorInfo
          author={post.author}
          userId={post.userId}
          highlightedAuthorHTML={isSearchPost ? post.highlight?.author : undefined}
        >
          <div className="inline max-h-[20px] lg:block">
            <span className="text-text-secondary">
              {timeSince(new Date(post.publishedAt ?? post.createdAt), {
                locale: user?.dashboardLocale ?? LocaleEnum.ENGLISH,
              })}
            </span>{' '}
            <span className="text-text-secondary">{t('community.pages_on')}</span>{' '}
            <Link className="font-semibold text-text-primary" href={topicPath} shallow>
              #{post.topic.name}
            </Link>
          </div>
        </PostAuthorInfo>
      }
      headerActions={
        <>
          <PostPinnedActions post={post} />
          <PostActionsMenu
            post={post}
            deletePost={deletePost}
            deletePostAndBanUser={deletePostAndBanUser}
          />
        </>
      }
      footerContent={
        <div className="flex flex-col gap-2">
          {post.status === PostStatusEnum.Approved && (
            <div className="mt-[7px] flex items-center gap-[2px]">
              {handleLikeClick && (
                <div onClick={handleWithoutPropagating()}>
                  <LikeButton
                    isLiked={post.isLiked}
                    likesCount={post.likesCount}
                    handleLikeClick={handleLikeClick}
                    postId={post.id}
                  />
                </div>
              )}
              <PostCommentsInfo info={commentsInfo} />
            </div>
          )}
          <CommentHighlightProvider>
            {comments?.map(comment => (
              <div
                key={comment.id}
                data-comment-id={comment.id}
                role="button"
                onClick={handleCommentClick}
              >
                <Comment data={comment} level={0} replies={comment.children} />
              </div>
            ))}
          </CommentHighlightProvider>
        </div>
      }
      extraContent={
        <>
          {opened && (
            <PostModal
              deletePost={deletePost}
              deletePostAndBanUser={deletePostAndBanUser}
              opened={opened}
              onClose={handleModalClose}
              post={post}
              handleLikeClick={handleLikeClick}
              commentsHighlightContext={commentsHighlightContext}
            />
          )}
        </>
      }
    />
  )
}

export default PostAnnounce
